
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Review } from '@/lib/types';
import { formatDistanceToNow } from 'date-fns';
import { Building, Briefcase, MapPin, Calendar, ThumbsUp, ThumbsDown } from 'lucide-react';

interface ReviewDialogProps {
  review: Review;
  isOpen: boolean;
  onClose: () => void;
}

const ReviewDialog = ({ review, isOpen, onClose }: ReviewDialogProps) => {
  const timeAgo = formatDistanceToNow(review.createdAt, { addSuffix: true });
  
  // Format exit date safely
  const formatExitDate = (exitDate: Date | undefined): string => {
    if (!exitDate) return '';
    const dateObj = exitDate instanceof Date ? exitDate : new Date(exitDate);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {review.logo ? (
              <img src={review.logo} alt={review.company} className="w-8 h-8 rounded-full" />
            ) : (
              <Building className="w-5 h-5 text-redflags-red" />
            )}
            <span>{review.company}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Job details */}
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {review.jobTitle && (
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-redflags-red" />
                <span className="font-medium">{review.jobTitle}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-redflags-gray" />
              <span>{review.location}</span>
            </div>
            
            {review.exitDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-redflags-gray" />
                <span>{formatExitDate(review.exitDate)}</span>
              </div>
            )}
            
            {review.positiveExperience !== undefined && (
              <div className="flex items-center gap-2">
                {review.positiveExperience ? (
                  <ThumbsUp className="w-4 h-4 text-green-500" />
                ) : (
                  <ThumbsDown className="w-4 h-4 text-redflags-red" />
                )}
                <span>
                  {review.positiveExperience 
                    ? "Positive exit experience" 
                    : "Negative exit experience"}
                </span>
              </div>
            )}
          </div>
          
          <Separator className="my-4" />
          
          {/* Reason for Exit */}
          {review.reasonForExit && (
            <div className="space-y-2">
              <h4 className="font-medium text-lg">Reason for Exit</h4>
              <p className="text-redflags-dark">{review.reasonForExit}</p>
            </div>
          )}
          
          {/* Biggest Problem */}
          {review.biggestProblem && (
            <div className="space-y-2 mt-6">
              <h4 className="font-medium text-lg">Biggest Problem</h4>
              <p className="text-redflags-dark">{review.biggestProblem}</p>
            </div>
          )}
          
          {/* Perk Ratings */}
          {review.perkRatings && review.perkRatings.length > 0 && (
            <div className="space-y-2 mt-6">
              <h4 className="font-medium text-lg">Company Perks Review</h4>
              <div className="space-y-3 mt-2">
                {review.perkRatings.map((rating, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-md">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium">{rating.perkText}</p>
                      {rating.isPositive !== null && (
                        rating.isPositive ? 
                          <ThumbsUp className="h-4 w-4 text-green-500" /> : 
                          <ThumbsDown className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    {rating.comment && (
                      <p className="text-sm text-gray-600">{rating.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="text-sm text-redflags-gray text-right mt-6">
            {timeAgo}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDialog;
